import { useState } from 'react';
import { Stack, Text, Button, Paper } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Patient, Encounter, Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import {
  PhysicalExaminationListItem,
  PhysicalExaminationAction,
} from './PhysicalExaminationListItem';
import { PhysicalExaminationInputModal } from './PhysicalExaminationInputModal';
import { type PhysicalExaminationData } from '@/components/inputs/physical-examination/types';
import {
  savePhysicalExamination,
  loadEncounterPhysicalExamination,
  deletePhysicalExamination,
} from '@/pages/ainigma/patient/examination/examination.helpers';
import { notifications } from '@mantine/notifications';
import { hasExaminationData } from '@/components/inputs/physical-examination/physical-examination.utils';

export interface PhysicalExaminationListProps {
  data: PhysicalExaminationData | null;
  onChange: (data: PhysicalExaminationData | null) => void;
  patient: Patient;
  encounter: Encounter;
}

export const PhysicalExaminationList = ({
  data,
  onChange,
  patient,
  encounter,
}: PhysicalExaminationListProps) => {
  const { t } = useTranslation(['patient-pages']);
  const medplum = useMedplum();
  const profile = useMedplumProfile();

  // Modal states
  const [modalOpened, setModalOpened] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');

  // Key to force re-render of list item after save (to expand sections)
  const [listItemKey, setListItemKey] = useState(0);

  // Check if we have any data
  const hasData = hasExaminationData(data);

  // Handlers
  const handleAdd = () => {
    setModalMode('add');
    setModalOpened(true);
  };

  const handleAction = async (action: PhysicalExaminationAction) => {
    if (action === 'edit') {
      setModalMode('edit');
      setModalOpened(true);
      return;
    }

    if (action === 'remove') {
      try {
        // Delete from server immediately
        await deletePhysicalExamination(medplum, patient, encounter);

        // Update local state
        onChange(null);

        // Show success notification
        notifications.show({
          color: 'green',
          title: t('examination.physicalExamination.deleteSuccess', {
            ns: 'patient-pages',
            defaultValue: 'Deleted',
          }),
          message: t('examination.physicalExamination.deleteSuccessMessage', {
            ns: 'patient-pages',
            defaultValue: 'Physical examination deleted successfully',
          }),
        });
      } catch (error) {
        console.error('Error deleting physical examination:', error);
        notifications.show({
          color: 'red',
          title: t('examination.physicalExamination.deleteError', {
            ns: 'patient-pages',
            defaultValue: 'Error',
          }),
          message: t('examination.physicalExamination.deleteErrorMessage', {
            ns: 'patient-pages',
            defaultValue: 'Failed to delete physical examination',
          }),
        });
      }
    }
  };

  const handleSave = async (savedData: PhysicalExaminationData) => {
    // Save immediately to the server
    await savePhysicalExamination(
      medplum,
      patient,
      encounter,
      savedData,
      profile as Practitioner | undefined
    );

    // Invalidate cache to force fresh fetches
    medplum.invalidateSearches('Observation');
  };

  const handleSaveSuccess = async () => {
    // Reload the data from the server to get the updated observations with IDs
    try {
      const { data: reloadedData } = await loadEncounterPhysicalExamination(
        medplum,
        patient,
        encounter
      );
      onChange(reloadedData);

      // Force re-render of list item to expand sections
      setListItemKey((prev) => prev + 1);

      // Show success notification
      notifications.show({
        color: 'green',
        title: t('examination.physicalExamination.saveSuccess', {
          ns: 'patient-pages',
          defaultValue: 'Saved',
        }),
        message: t('examination.physicalExamination.saveSuccessMessage', {
          ns: 'patient-pages',
          defaultValue: 'Physical examination saved successfully',
        }),
      });
    } catch (error) {
      console.error('Error reloading physical examination:', error);
    }
  };

  return (
    <Stack gap="md">
      {/* Header */}
      <Text size="xl" fw={500}>
        {t('examination.physicalExamination.title', {
          ns: 'patient-pages',
          defaultValue: 'Physical Examination',
        })}
      </Text>

      {/* Empty state or display examination */}
      {!hasData ? (
        <>
          <Paper withBorder radius="md" p="xl" bg="gray.0">
            <Text size="sm" c="dimmed" ta="center">
              {t('examination.physicalExamination.noExamination', {
                ns: 'patient-pages',
                defaultValue: 'No physical examination recorded',
              })}
            </Text>
          </Paper>
          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            size="sm"
            onClick={handleAdd}
          >
            {t('examination.physicalExamination.addExamination', {
              ns: 'patient-pages',
              defaultValue: 'Add Examination',
            })}
          </Button>
        </>
      ) : (
        data && (
          <PhysicalExaminationListItem
            key={listItemKey}
            data={data}
            onAction={handleAction}
            isDeleting={false}
          />
        )
      )}

      {/* Modal */}
      <PhysicalExaminationInputModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        onSave={handleSave}
        onSaveSuccess={handleSaveSuccess}
        data={hasData && data ? data : undefined}
        mode={modalMode}
      />
    </Stack>
  );
};
